import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { BiDotsHorizontal } from 'react-icons/bi';
import ReactLoading from 'react-loading';

// Utility imports
import AuthService from '../../../../services/auth';
import { useGet } from '../../../../reducers/useGet';
import { useGetWithRefresh } from '../../../../reducers/useGetWithRefresh';
import downloadCSV from '../../../../scripts/export-script';

// Component imports
import SelectDataButton from '../../date-selector';
import ErrorMessage from '../../messages-panel/error-message';
import NoDataAvailableMessage from '../../messages-panel/no-data-available-message';
import ExportButton from '../../../data-table-custom/export-button';
import { BarChart1Element } from '../../charts';

// Styles
import './water-flow-data-animal-style.css';
import { Hidrometer } from './hidrometer';
import { AccumulatedValue } from './accumulated-value';
import { isNumber } from 'lodash';
import { hidrometer } from '../../../../sensors-type';

// Constantes
const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';
const STATUS_COLORS = {
	ONLINE_NORMAL: 'green',
	ONLINE_ALERT: 'red',
	OFFLINE: 'gray',
};

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutos

export default function WaterFlowDataAnimalPanel({ sensor, panelType }) {
	const { access_token } = AuthService.getCurrentUser();
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const [hidrometerValue, setHidrometerValue] = useState(NaN); // valor do hidrômetro

	// Estados para os dados do gráfico diário
	const [dailyChartData, setDailyChartData] = useState([]); // Para o Gráfico Diário do Fluxo de Água
	const [dailyChartPeriod1, setDailyChartPeriod1] = useState(() =>
		moment().startOf('day').subtract(7, 'days').toDate()
	);
	const [dailyChartPeriod2, setDailyChartPeriod2] = useState(() =>
		moment().subtract(1, 'day').endOf('day').toDate()
	);

	// Estados para os dados do gráfico horário
	const [hourlyChartData, setHourlyChartData] = useState([]); // Para o Gráfico Horário do Fluxo de Água
	const [hourlyChartPeriod1, setHourlyChartPeriod1] = useState(() =>
		moment().subtract(24, 'hours').toDate()
	);
	const [hourlyChartPeriod2, setHourlyChartPeriod2] = useState('');

	// Estados para os dados do valor acumulado
	const [accumulatedValue, setAccumulatedValue] = useState(NaN);
	const [accPeriod1, setAccPeriod1] = useState(() => moment().startOf('day').toDate());
	const [accPeriod2, setAccPeriod2] = useState('');

	// Estados para dados e UI
	const [showChart, setShowChart] = useState(false); // controle de exibição dos gráficos
	const [statusColor, setStatusColor] = useState(STATUS_COLORS.OFFLINE); // cor do status

	// Constrói a requisição API para dados acumulados
	const apiRequestAccumulatedValue = useMemo(() => {
		const data1 = moment(accPeriod1).utc().format(DATE_FORMAT);

		// Se accPeriod2 estiver definido, inclui na requisição; caso contrário, omite
		if (accPeriod2) {
			const data2 = moment(accPeriod2).utc().format(DATE_FORMAT);
			return `consulta_poco?tipo_mensagem=1&sensor=${sensor.id_dispositivo}&data:>=${data1}&data:<=${data2}`;
		}

		// Não inclui data final, o backend usará a data atual
		return `consulta_poco?tipo_mensagem=1&sensor=${sensor.id_dispositivo}&data:>=${data1}`;
	}, [accPeriod1, accPeriod2, sensor.id_dispositivo]);

	// Constrói a requisição API para dados do gráfico diário
	const apiRequestDailyChartData = useMemo(() => {
		const data1 = moment(dailyChartPeriod1).utc().format(DATE_FORMAT);

		if (dailyChartPeriod2 !== '') {
			const data2 = moment(dailyChartPeriod2).utc().format(DATE_FORMAT);
			return `dados_diarios_hidro_consulta?sensor=${sensor.id_dispositivo}&data:>=${data1}&data:<=${data2}`;
		}

		return `dados_diarios_hidro_consulta?sensor=${sensor.id_dispositivo}&data:>=${data1}`;
	}, [dailyChartPeriod1, dailyChartPeriod2, sensor.id_dispositivo]);

	// Constrói a requisição API para dados do gráfico horário
	const apiRequestHourlyChartData = useMemo(() => {
		const data1 = moment(hourlyChartPeriod1).utc().format(DATE_FORMAT);

		if (hourlyChartPeriod2 !== '') {
			const data2 = moment(hourlyChartPeriod2).utc().format(DATE_FORMAT);
			return `dados_horarios_hidro_consulta?sensor=${sensor.id_dispositivo}&data:>=${data1}&data:<=${data2}`;
		}

		return `dados_horarios_hidro_consulta?sensor=${sensor.id_dispositivo}&data:>=${data1}`;
	}, [hourlyChartPeriod1, hourlyChartPeriod2, sensor.id_dispositivo]);

	// Hooks para requisições API - usando o hook com refresh trigger
	const getLastData = useGetWithRefresh(
		`evento_consulta?sensor=${sensor.id_dispositivo}&ultimo_dado=1&tipo_mensagem=1`,
		access_token,
		refreshTrigger
	);
	const getDailyChartData = useGetWithRefresh(
		apiRequestDailyChartData,
		access_token,
		showChart ? refreshTrigger : 0 // Só faz refresh do gráfico se estiver visível
	);
	const getHourlyChartData = useGetWithRefresh(
		apiRequestHourlyChartData,
		access_token,
		showChart ? refreshTrigger : 0 // Só faz refresh do gráfico se estiver visível
	);

	const getModelo = useGet(
		`modelo_consulta?modelo_id=${sensor.modelo}`,
		access_token // Modelo não precisa de refresh constante
	);
	const getDataAcc = useGetWithRefresh(apiRequestAccumulatedValue, access_token, refreshTrigger);

	// Auto-refresh logic com controles mais inteligentes
	useEffect(() => {
		const intervalId = setInterval(() => {
			// Só faz refresh se não estiver já refreshing e se a aba estiver visível
			if (!isRefreshing && !document.hidden) {
				setIsRefreshing(true);
				setRefreshTrigger((prev) => prev + 1);
			}
		}, REFRESH_INTERVAL);

		return () => clearInterval(intervalId);
	}, [isRefreshing]);

	// Detecta quando as requisições terminaram para resetar o estado de refreshing
	useEffect(() => {
		const allRequestsCompleted =
			!getLastData.state.loading &&
			!getDataAcc.state.loading &&
			(!showChart || !getDailyChartData.state.loading || !getHourlyChartData.state.loading);

		if (isRefreshing && allRequestsCompleted) {
			setIsRefreshing(false);
		}
	}, [
		getLastData.state.loading,
		getDataAcc.state.loading,
		getDailyChartData.state.loading,
		getHourlyChartData.state.loading,
		isRefreshing,
		showChart,
	]);

	// Processamento dos dados para gráfico e valor acumulado
	useEffect(() => {
		// Verifica se os dados essenciais já foram carregados
		const isDataReady =
			!getDataAcc.state.loading &&
			!getDataAcc.state.error &&
			getDataAcc.state.data &&
			!getModelo.state.loading &&
			!getModelo.state.error &&
			getModelo.state.data?.length > 0;

		if (!isDataReady) return;

		// Define o valor atual do hidrômetro
		if (getLastData.state.data && getLastData.state.data.length > 0 && sensor.tipo === hidrometer)
			setHidrometerValue(Number(getLastData.state.data[0].valor_sensor));

		// Pega o valor acumulado (diferença entre último e primeiro valor)
		setAccumulatedValue(getDataAcc.state.data.valor_acumulado);

		// Processa dados dos gráficos apenas se estiverem disponíveis e showChart estiver ativo
		if (showChart && getDailyChartData.state.data && getDailyChartData.state.data.length > 0) {
			const dailyChartDataAuxArray = getDailyChartData.state.data.map((d) => {
				if (!d.data || !isNumber(d.acc_poco)) return null;
				return {
					timestamp: moment(d.data).format(DATE_FORMAT),
					value: +d.acc_poco,
				};
			});
			setDailyChartData(dailyChartDataAuxArray.filter(Boolean)); // Remove valores null
		}

		if (showChart && getHourlyChartData.state.data && getHourlyChartData.state.data.length > 0) {
			const hourlyChartDataAuxArray = getHourlyChartData.state.data.map((d) => {
				if (!d.data || !isNumber(d.acc_poco)) return null;
				return {
					timestamp: moment(d.data).format(DATE_FORMAT),
					value: +d.acc_poco,
				};
			});
			setHourlyChartData(hourlyChartDataAuxArray.filter(Boolean)); // Remove valores null
		}
	}, [
		getDataAcc.state.loading,
		getDataAcc.state.error,
		getDataAcc.state.data,
		getDailyChartData.state.data,
		getHourlyChartData.state.data,
		getModelo.state.loading,
		getModelo.state.error,
		getModelo.state.data,
		getLastData.state.data,
		sensor.tipo,
		showChart,
	]);

	// Processamento do status baseado no último dado
	useEffect(() => {
		if (getLastData.state.loading || !getLastData.state.data?.length) return;

		const now = new Date();

		// Converte o timestamp do último dado
		const lastTimestampToLastData = moment.unix(getLastData.state.data[0].timestamp / 1000);
		const lastDataTime = new Date(lastTimestampToLastData);

		// Calcula a diferença de tempo em minutos
		const millisecondsDiff = now - lastDataTime;
		const minutesDiff = millisecondsDiff / 1000 / 60;

		// Determina a cor do status
		if (minutesDiff < 120) {
			// Online (dentro de 2h)
			setStatusColor(
				accumulatedValue < sensor.alerta_superior
					? STATUS_COLORS.ONLINE_NORMAL
					: STATUS_COLORS.ONLINE_ALERT
			);
		} else setStatusColor(STATUS_COLORS.OFFLINE); // Offline por mais de 2h
	}, [getLastData.state.loading, getLastData.state.data, sensor.alerta_superior, accumulatedValue]);

	// Componente de exportação
	const Export = ({ onExport }) => <ExportButton onExport={onExport} chartPanel={true} />;

	// Define o tipo de painel baseado na prop
	const divPanelType =
		panelType === 'waterlogging' ? 'little-panel-container' : 'little-panel-container-pit';

	// Verificação de estado de carregamento
	const isLoading =
		getDataAcc.state.loading || getDailyChartData.state.loading || getModelo.state.loading;

	// Verificação de estado de erro
	const hasError = getDataAcc.state.error || getDailyChartData.state.error || getModelo.state.error;

	// Renderiza o componente de carregamento
	if (isLoading) {
		return (
			<div className={divPanelType}>
				<div className="panel-title-container">
					<em className="panel-title">{sensor.tag}</em>
				</div>
				<div className="panel-loading">
					<ReactLoading
						type="spinningBubbles"
						color="var(--main-color)"
						width="50px"
						height="50px"
					/>
				</div>
			</div>
		);
	}

	// Renderiza mensagem de erro
	if (hasError) {
		return (
			<div className={divPanelType}>
				<div className="panel-title-container">
					<em className="panel-title">{sensor.tag}</em>
				</div>
				<ErrorMessage />
			</div>
		);
	}

	// Renderiza o conteúdo principal
	return (
		<div className={divPanelType}>
			<div className="panel-title-container">
				<em className="panel-title">{sensor.tag}</em>
			</div>
			<div style={{ margin: '10px' }}>
				{sensor.tipo === hidrometer ? (
					// Mostrar o hidrômetro apenas para o sensor do tipo "Hidrômetro"
					<Hidrometer
						sensor={sensor}
						hidrometerValue={hidrometerValue}
						accumulatedValue={accumulatedValue}
						accPeriod1={accPeriod1}
						setAccPeriod1={setAccPeriod1}
						accPeriod2={accPeriod2}
						setAccPeriod2={setAccPeriod2}
						STATUS_COLORS={STATUS_COLORS}
						statusColor={statusColor}
					/>
				) : (
					// Mostrar apenas o valor acumulado caso contrário"
					<AccumulatedValue
						sensor={sensor}
						accumulatedValue={accumulatedValue}
						accPeriod1={accPeriod1}
						setAccPeriod1={setAccPeriod1}
						accPeriod2={accPeriod2}
						setAccPeriod2={setAccPeriod2}
						STATUS_COLORS={STATUS_COLORS}
						statusColor={statusColor}
					/>
				)}

				<div style={{ paddingBottom: '10px' }}>
					{/* Botão de detalhes */}
					<button className="more-details-button" onClick={() => setShowChart(!showChart)}>
						+ Detalhes
					</button>
					{/* Seção de gráfico (expansível) */}
					{showChart && (
						<div>
							{/* Gráfico Diário do Fluxo de Água */}
							<div>
								<div
									style={{
										width: '100%',
										height: '1.5px',
										backgroundColor: 'black',
										borderRadius: '20px',
										margin: '10px 0px 5px 0px',
									}}
								/>
								<div className="panel-items">
									<BiDotsHorizontal />
									<span className="panel-items-title">Gráfico Diário do Fluxo de Água</span>
								</div>
								<SelectDataButton
									periodo1={dailyChartPeriod1}
									setPeriodo1={setDailyChartPeriod1}
									periodo2={dailyChartPeriod2}
									setPeriodo2={setDailyChartPeriod2}
									hourlyData={false}
								/>
								{dailyChartData.length > 0 ? (
									<BarChart1Element
										data={dailyChartData}
										unit={'m³'}
										color={'#187BCD'}
										fileName={'grafico-fluxo-agua-diaria'}
									/>
								) : (
									<NoDataAvailableMessage />
								)}
								{dailyChartData.length > 0 && (
									<div className="panel-items">
										<div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
											<div>
												<BiDotsHorizontal />
												<span className="panel-items-title">Exportar dados do Gráfico</span>
											</div>
											<Export
												onExport={() =>
													downloadCSV(dailyChartData, `dados-hidrometro-NS${sensor.serie}`)
												}
											/>
										</div>
									</div>
								)}
							</div>

							{/* Gráfico Horário do Fluxo de Água */}
							<div>
								<div
									style={{
										width: '100%',
										height: '1.5px',
										backgroundColor: 'black',
										borderRadius: '20px',
										margin: '10px 0px 5px 0px',
									}}
								/>
								<div className="panel-items">
									<BiDotsHorizontal />
									<span className="panel-items-title">Gráfico Horário do Fluxo de Água</span>
								</div>
								<SelectDataButton
									periodo1={hourlyChartPeriod1}
									setPeriodo1={setHourlyChartPeriod1}
									periodo2={hourlyChartPeriod2}
									setPeriodo2={setHourlyChartPeriod2}
									hourlyData={true}
								/>
								{hourlyChartData.length > 0 ? (
									<BarChart1Element
										data={hourlyChartData}
										unit={'m³'}
										color={'#187BCD'}
										fileName={'grafico-fluxo-agua-horário'}
									/>
								) : (
									<NoDataAvailableMessage />
								)}
								{hourlyChartData.length > 0 && (
									<div className="panel-items">
										<div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
											<div>
												<BiDotsHorizontal />
												<span className="panel-items-title">Exportar dados do Gráfico</span>
											</div>
											<Export
												onExport={() =>
													downloadCSV(hourlyChartData, `dados-hidrometro-NS${sensor.serie}`)
												}
											/>
										</div>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
